import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import { useUIStore } from '../../stores/uiStore';
import { useFileStore, type FileItem } from '../../stores/fileStore';
import { filesApi } from '../../api/files';
import { Spinner } from '../ui/Spinner';
import { useToast } from '../ui/Toaster';
import { formatBytes } from '../../utils/dateFormat';

function getFileCategory(mimeType: string): 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'other' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'pdf';
  if (
    mimeType.startsWith('text/') ||
    mimeType === 'application/json' ||
    mimeType === 'application/javascript' ||
    mimeType === 'application/xml'
  ) {
    return 'text';
  }
  return 'other';
}

export function FilePreviewModal() {
  const { t } = useTranslation('files');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewFile = useUIStore((s) => s.previewFile);
  const setPreviewFile = useUIStore((s) => s.setPreviewFile);
  const files = useFileStore((s) => s.files);
  const toast = useToast();

  const isOpen = previewFile !== null;
  const currentIndex = previewFile ? files.findIndex((f) => f.id === previewFile.id) : -1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < files.length - 1;

  const loadPreview = useCallback(async (file: FileItem) => {
    setIsLoading(true);
    setError(null);
    setPreviewUrl(null);
    setTextContent(null);

    try {
      const { downloadUrl } = await filesApi.getDownloadUrl(file.id);
      const category = getFileCategory(file.mimeType);

      if (category === 'text') {
        // Fetch text content
        const response = await fetch(downloadUrl);
        const text = await response.text();
        setTextContent(text.slice(0, 50000)); // Limit to 50KB of text
      } else {
        setPreviewUrl(downloadUrl);
      }
    } catch {
      setError(t('preview.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (previewFile) {
      loadPreview(previewFile);
    }
  }, [previewFile, loadPreview]);

  const handleClose = () => {
    setPreviewFile(null);
    setPreviewUrl(null);
    setTextContent(null);
    setError(null);
  };

  const handlePrevious = () => {
    if (hasPrevious) {
      setPreviewFile(files[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      setPreviewFile(files[currentIndex + 1]);
    }
  };

  const handleDownload = async () => {
    if (!previewFile) return;
    try {
      const { downloadUrl } = await filesApi.getDownloadUrl(previewFile.id);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = previewFile.name;
      a.click();
    } catch {
      toast.error(t('toast.downloadFailed'));
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowLeft' && hasPrevious) {
        handlePrevious();
      } else if (e.key === 'ArrowRight' && hasNext) {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasPrevious, hasNext]);

  if (!previewFile) return null;

  const category = getFileCategory(previewFile.mimeType);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/90"
            onClick={handleClose}
          />

          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/50 to-transparent">
            <div className="flex items-center gap-3 text-white">
              <h3 className="font-medium truncate max-w-md">{previewFile.name}</h3>
              <span className="text-white/60 text-sm">{formatBytes(previewFile.size)}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                className="p-2 rounded-lg hover:bg-white/10 text-white transition-colors"
                title={t('common:buttons.download')}
              >
                <ArrowDownTrayIcon className="w-5 h-5" />
              </button>
              <button
                onClick={handleClose}
                className="p-2 rounded-lg hover:bg-white/10 text-white transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Navigation arrows */}
          {hasPrevious && (
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <ChevronLeftIcon className="w-8 h-8" />
            </button>
          )}
          {hasNext && (
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <ChevronRightIcon className="w-8 h-8" />
            </button>
          )}

          {/* Content */}
          <div className="relative z-0 w-full h-full flex items-center justify-center p-16">
            {isLoading ? (
              <Spinner size="lg" />
            ) : error ? (
              <div className="text-center text-white">
                <DocumentIcon className="w-16 h-16 mx-auto mb-4 text-white/50" />
                <p className="text-white/70">{error}</p>
              </div>
            ) : (
              <motion.div
                key={previewFile.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="max-w-full max-h-full"
              >
                {category === 'image' && previewUrl && (
                  <img
                    src={previewUrl}
                    alt={previewFile.name}
                    className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                  />
                )}

                {category === 'video' && previewUrl && (
                  <video
                    src={previewUrl}
                    controls
                    autoPlay
                    className="max-w-full max-h-[80vh] rounded-lg shadow-2xl"
                  >
                    {t('preview.videoNotSupported')}
                  </video>
                )}

                {category === 'audio' && previewUrl && (
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 min-w-[400px]">
                    <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg
                        className="w-16 h-16 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                      </svg>
                    </div>
                    <audio src={previewUrl} controls autoPlay className="w-full">
                      {t('preview.audioNotSupported')}
                    </audio>
                  </div>
                )}

                {category === 'pdf' && previewUrl && (
                  <div className="flex flex-col items-center">
                    <object
                      data={previewUrl}
                      type="application/pdf"
                      className="w-[90vw] h-[80vh] rounded-lg shadow-2xl bg-white"
                    >
                      {/* Fallback si el navegador no puede mostrar el PDF */}
                      <div className="flex flex-col items-center justify-center h-full bg-slate-800 rounded-lg p-8">
                        <DocumentIcon className="w-24 h-24 text-red-400 mb-4" />
                        <p className="text-white text-lg font-medium mb-2">{previewFile.name}</p>
                        <p className="text-white/60 mb-4">{t('preview.pdfNotAvailable')}</p>
                      </div>
                    </object>
                    <button
                      onClick={() => window.open(previewUrl, '_blank')}
                      className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors flex items-center gap-2"
                    >
                      <ArrowDownTrayIcon className="w-5 h-5" />
                      {t('preview.openInNewTab')}
                    </button>
                  </div>
                )}

                {category === 'text' && textContent !== null && (
                  <div className="bg-slate-900 rounded-lg shadow-2xl overflow-hidden max-w-4xl w-full">
                    <div className="bg-slate-800 px-4 py-2 border-b border-slate-700">
                      <span className="text-slate-400 text-sm">{previewFile.name}</span>
                    </div>
                    <pre className="p-4 text-sm text-slate-200 overflow-auto max-h-[70vh] font-mono whitespace-pre-wrap">
                      {textContent}
                    </pre>
                  </div>
                )}

                {category === 'other' && (
                  <div className="text-center text-white">
                    <DocumentIcon className="w-24 h-24 mx-auto mb-4 text-white/50" />
                    <p className="text-lg font-medium mb-2">{previewFile.name}</p>
                    <p className="text-white/60 mb-4">{formatBytes(previewFile.size)}</p>
                    <p className="text-white/50 text-sm">{t('preview.notAvailable')}</p>
                    <button
                      onClick={handleDownload}
                      className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                    >
                      <ArrowDownTrayIcon className="w-5 h-5 inline-block mr-2" />
                      {t('common:buttons.download')}
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Footer with navigation info */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 text-white/60 text-sm">
            {currentIndex + 1} / {files.length}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
